import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building } from "lucide-react";
import { useUiPrefs } from "@/lib/uiPrefs";
import { DensityToggle, tableDensity } from "@/components/ops-ui";

const empty = { name: "", registration_no: "", tax_no: "", address: "", email: "", phone: "", is_supplier_side: false };

export default function BillingCompanies() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const { density, setDensity } = useUiPrefs("billing-companies");

  const load = () => api.get("/billing-companies").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm(r); setOpen(true); };
  const save = async () => {
    try {
      if (editing) await api.put(`/billing-companies/${editing.id}`, form);
      else await api.post("/billing-companies", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await api.delete(`/billing-companies/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <header className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="caption">Accounting</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Billing Companies</h1>
          <p className="text-slate-500 mt-1 text-sm">Legal entities used on product invoices and freight-charges invoices for Bukku sync.</p>
        </div>
        <div className="flex items-center gap-2">
        <DensityToggle density={density} setDensity={setDensity} testid="bc-density-toggle" />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="bc-new-button" onClick={openNew} className="bg-sky-600 hover:bg-sky-700"><Plus className="w-4 h-4 mr-1.5" />New</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Edit company" : "New billing company"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Name *</Label><Input data-testid="bc-name-input" value={form.name} onChange={e=>setForm({...form, name:e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Registration No.</Label><Input value={form.registration_no} onChange={e=>setForm({...form, registration_no:e.target.value})} /></div>
                <div><Label>Tax No.</Label><Input value={form.tax_no} onChange={e=>setForm({...form, tax_no:e.target.value})} /></div>
              </div>
              <div><Label>Address</Label><Textarea rows={2} value={form.address} onChange={e=>setForm({...form, address:e.target.value})} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input value={form.email} onChange={e=>setForm({...form, email:e.target.value})} /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} /></div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.is_supplier_side} onCheckedChange={v=>setForm({...form, is_supplier_side: !!v})} />
                Supplier-side (freight courier). Uncheck for our own billing entity.
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button onClick={save} data-testid="bc-save-button" className="bg-slate-900">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </header>

      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className={`excel-table w-full ${tableDensity(density)}`} data-testid="bc-table">
          <thead><tr><th>Name</th><th>Type</th><th>Reg No</th><th>Tax No</th><th>Contact</th><th></th></tr></thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id}>
                <td className="font-semibold">{r.name}</td>
                <td><span className={`px-2 py-0.5 rounded text-xs font-medium ${r.is_supplier_side ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`}>{r.is_supplier_side ? "Freight courier" : "Our entity"}</span></td>
                <td className="font-mono text-xs">{r.registration_no || "—"}</td>
                <td className="font-mono text-xs">{r.tax_no || "—"}</td>
                <td className="text-xs text-slate-500">{r.email || r.phone || "—"}</td>
                <td className="text-right">
                  <Button variant="ghost" size="sm" onClick={()=>openEdit(r)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={()=>del(r.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (<tr><td colSpan={6} className="text-center py-12 text-slate-400"><Building className="w-8 h-8 mx-auto mb-2" />No companies yet.</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}
